import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axiosConfig';
import './dailyIssueStockExplorer.css';

type StockRow = {
  ticker: string;
  name: string;
  issueCount: number;
  latestPublishedAt: string | null;
};
type IssueRow = {
  id: string;
  title: string;
  summary: string;
  source: string;
  url: string;
  publishedAt: string | null;
};
type StockResponse = { date: string; totalStocks: number; items: StockRow[] };
type IssueResponse = { date: string; ticker: string; truncated: boolean; items: IssueRow[] };

function isoKstToday(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const field = (name: string) => parts.find((part) => part.type === name)?.value ?? '';
  return `${field('year')}-${field('month')}-${field('day')}`;
}

function shiftedDate(day: string, delta: number): string {
  const value = new Date(`${day}T12:00:00Z`);
  if (!Number.isFinite(value.getTime())) return day;
  value.setUTCDate(value.getUTCDate() + delta);
  return value.toISOString().slice(0, 10);
}

function sourceUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local') || host.endsWith('.internal')) return null;
    if (/^(?:127|10|0|192\.168)\./.test(host)) return null;
    if (/^172\.(?:1[6-9]|2\d|3[01])\./.test(host)) return null;
    if (host === '::1' || host === '[::1]' || host === '[::]') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function shortDateTime(value: string | null): string {
  if (!value) return '시간 미확인';
  // The API returns the same DB-local (KST) clock convention as the existing issue feed.
  return value.replace('T', ' ').slice(11, 16) || value.slice(0, 10);
}

export default function DailyIssueStockExplorer(): React.ReactElement {
  const today = useMemo(isoKstToday, []);
  const [day, setDay] = useState(today);
  const [reloadKey, setReloadKey] = useState(0);
  const [rows, setRows] = useState<StockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [ticker, setTicker] = useState('');
  const [issueRows, setIssueRows] = useState<IssueRow[]>([]);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueError, setIssueError] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    setRows([]);
    void api.get<StockResponse>('/live-summary/issues/daily-stocks', {
      params: { date: day }, withCredentials: true, signal: controller.signal, timeout: 12000,
    }).then((result) => {
      if (controller.signal.aborted) return;
      setRows(Array.isArray(result.data?.items) ? result.data.items : []);
    }).catch(() => {
      if (!controller.signal.aborted) setLoadError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [day, reloadKey]);

  useEffect(() => {
    setTicker('');
    setIssueRows([]);
  }, [day]);

  useEffect(() => {
    if (!ticker) return undefined;
    const controller = new AbortController();
    setIssueLoading(true);
    setIssueError(false);
    setIssueRows([]);
    setTruncated(false);
    void api.get<IssueResponse>(`/live-summary/issues/daily-stocks/${ticker}`, {
      params: { date: day }, withCredentials: true, signal: controller.signal, timeout: 12000,
    }).then((result) => {
      if (controller.signal.aborted) return;
      setIssueRows(Array.isArray(result.data?.items) ? result.data.items : []);
      setTruncated(Boolean(result.data?.truncated));
    }).catch(() => {
      if (!controller.signal.aborted) setIssueError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setIssueLoading(false);
    });
    return () => controller.abort();
  }, [day, ticker, reloadKey]);

  const selected = rows.find((row) => row.ticker === ticker);
  const totalMapped = rows.reduce((sum, row) => sum + Math.max(0, row.issueCount), 0);

  return (
    <section className="pi-daily-issue-stocks" aria-label="날짜별 이슈 종목 탐색">
      <header className="pi-daily-issue-stocks__head">
        <div>
          <small>이슈 종목 아카이브</small>
          <h3>날짜별 이슈 종목</h3>
          <p>종목 중복 제거 · 클릭하면 해당 날짜 이슈만 표시</p>
        </div>
        <div className="pi-daily-issue-stocks__date">
          <button type="button" aria-label="이전 날짜" onClick={() => setDay((value) => shiftedDate(value, -1))}>‹</button>
          <input type="date" aria-label="조회 날짜" value={day} max={today} onChange={(event) => {
            if (event.target.value) setDay(event.target.value);
          }} />
          <button type="button" aria-label="다음 날짜" disabled={day >= today} onClick={() => setDay((value) => shiftedDate(value, 1))}>›</button>
          {day !== today && <button type="button" onClick={() => setDay(today)}>오늘</button>}
          <button type="button" onClick={() => setReloadKey((current) => current + 1)} aria-label="이슈 종목 다시 조회">갱신</button>
        </div>
      </header>
      <p className="pi-daily-issue-stocks__note">뉴스와 종목의 직접 연결을 집계합니다. 이슈의 사실 검증이나 주가 방향을 보증하지 않습니다.</p>
      {loading ? <p className="pi-daily-issue-stocks__status" role="status">이슈 종목을 조회하고 있습니다.</p>
        : loadError ? <p className="pi-daily-issue-stocks__status is-error" role="alert">이슈 종목 API를 조회하지 못했습니다. 일반 이슈 목록은 아래에서 계속 확인할 수 있습니다.</p>
          : rows.length === 0 ? <p className="pi-daily-issue-stocks__status">{day}에 직접 연결된 종목 이슈가 없습니다. 다른 날짜를 선택하거나 아래 전체 이슈를 확인하세요.</p>
            : <>
                <p className="pi-daily-issue-stocks__count">{day} · {rows.length.toLocaleString()}종목 · 종목별 연결 {totalMapped.toLocaleString()}건</p>
                <div className="pi-daily-issue-stocks__list" aria-label="중복 제거 종목">
                  {rows.map((row) => (
                    <button key={row.ticker} type="button" className={row.ticker === ticker ? 'is-active' : ''}
                      aria-pressed={row.ticker === ticker} onClick={() => setTicker((current) => current === row.ticker ? '' : row.ticker)}>
                      <span><b>{row.name || row.ticker}</b><small>{row.ticker}</small></span>
                      <span className="pi-daily-issue-stocks__badge">{row.issueCount}건</span>
                    </button>
                  ))}
                </div>
              </>}
      {ticker && selected && (
        <div className="pi-daily-issue-stocks__detail">
          <div className="pi-daily-issue-stocks__detail-head">
            <div><b>{selected.name || selected.ticker}</b><small>{day} 이슈 · {selected.issueCount}건</small></div>
            <button type="button" onClick={() => setTicker('')} aria-label="종목 이슈 닫기">닫기 ×</button>
          </div>
          {issueLoading ? <p role="status">해당 종목의 이슈를 조회 중입니다.</p>
            : issueError ? <p role="alert">이 종목의 이슈 상세를 불러오지 못했습니다.</p>
              : issueRows.length === 0 ? <p>해당 날짜에 표시할 이슈가 없습니다.</p>
                : <div className="pi-daily-issue-stocks__articles">
                    {issueRows.map((issue) => {
                      const url = sourceUrl(issue.url);
                      return <article key={issue.id}>
                        <div><time>{shortDateTime(issue.publishedAt)}</time><span>{issue.source}</span></div>
                        <b>{issue.title}</b>
                        {issue.summary && <p>{issue.summary}</p>}
                        {url && <a href={url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">출처 확인 ↗</a>}
                      </article>;
                    })}
                    {truncated && <p>이슈가 500건을 초과해 최신 500건만 표시됩니다. 전체 이력 확인은 추후 페이지 조회가 필요합니다.</p>}
                  </div>}
        </div>
      )}
    </section>
  );
}
